import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, CalendarClock, ClipboardList, ListChecks, Clock, Bell, ShieldCheck, PackageMinus,
} from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import {
  PriorityBadge, FaultStatusBadge, ReplacementBadge, InspectionStatusBadge, formatNlDate,
} from "@/components/Badges";
import { Code } from "@/components/Code";
import { Page, DowntimeClock } from "@/components/PageConsole";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// One count per tile. Exactly one of them is `display`; the rest are `kop`,
// because a screen is about one number. A tile that names a filtered list
// takes an `onClick` (§8): the number and its list must be one action away.
const StatCard = ({ icon: Icon, label, value, tone, testid, alarm, display, onClick }) => {
  const inner = (
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded ${tone}`}>
        <Icon size={18} weight={alarm && value > 0 ? "fill" : "regular"} />
      </div>
      <div className="min-w-0">
        <div className={`font-mono tabular-nums text-foreground ${display ? "text-display" : "text-kop"}`}>
          {value}
        </div>
        <div className="text-label text-muted-foreground">{label}</div>
      </div>
    </div>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testid} className="w-full text-left">
        <Card className="p-4 transition-colors hover:bg-accent">{inner}</Card>
      </button>
    );
  }
  return <Card className="p-4" data-testid={testid}>{inner}</Card>;
};

const SectionHead = ({ icon: Icon, children, linkLabel, onLink, testid }) => (
  <div className="mb-3 flex items-center justify-between gap-2">
    <h2 className="flex items-center gap-2 text-kop text-foreground">
      {Icon && <Icon size={20} />} {children}
    </h2>
    {linkLabel && (
      <button type="button" className="text-body text-primary hover:underline" onClick={onLink} data-testid={testid}>
        {linkLabel}
      </button>
    )}
  </div>
);

const ageText = (iso) => {
  if (!iso) return null;
  const hours = Math.max(0, (Date.now() - new Date(iso).getTime()) / 36e5);
  return hours >= 24 ? `${Math.round(hours / 24)} dagen open` : `${Math.round(hours)} uur open`;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { canView } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try { const res = await api.get("/dashboard"); setData(res.data); }
    catch (e) { setError(getErrorMessage(e, "Kan dashboard niet laden.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // A counter with value zero is not shown: no tile, no card, no section.
  const tiles = useMemo(() => {
    if (!data) return [];
    return [
      { show: canView("storingen"), value: data.open_fault_count, testid: "stat-open", icon: ClipboardList, label: "Open storingen", tone: "bg-zebra text-inkt-zacht", short: "storingen", onClick: () => navigate("/storingen") },
      { show: canView("keuringen"), value: data.inspection_count, testid: "stat-inspections", icon: ShieldCheck, label: "Keuringen verlopen of binnenkort", tone: "bg-zebra text-inkt-zacht", short: "keuringen", onClick: () => navigate("/keuringen") },
      { show: canView("onderdelen"), value: data.low_stock_count ?? 0, testid: "stat-lowstock", icon: PackageMinus, label: "Onderdelen onder minimum", tone: "bg-fout-vlak text-fout-tekst", alarm: true, short: "voorraad", onClick: () => navigate("/onderdelen?onder=1") },
      { show: true, value: data.aanvullen_count, testid: "stat-aanvullen", icon: CalendarClock, label: "Assets met 'Aanvullen'", tone: "bg-zebra text-inkt-zacht", short: "aanvullen", onClick: () => navigate("/assets?aanvullen=1") },
    ].filter((t) => t.show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const emptyLabels = useMemo(() => {
    if (!data) return [];
    const empty = tiles.filter((t) => !t.value).map((t) => t.short);
    if (canView("kalender") && !data.today_task_count && !data.overdue_task_count) empty.push("taken");
    if (canView("punten") && !(data.open_punt_count ?? 0)) empty.push("punten");
    if (canView("werkbonnen") && !(data.open_werkbon_count ?? 0)) empty.push("werkbonnen");
    return empty;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tiles]);

  const topP1 = (canView("storingen") && data?.open_faults?.find((f) => f.priority === "P1")) || null;
  const shown = tiles.filter((t) => t.value > 0);
  // The display step goes to the P1 number when there is one; otherwise to the
  // loudest count. Never to the word "Dashboard".
  const displayTile = topP1 ? null : shown[0]?.testid;

  // Facts, never a sentence. A live P1 replaces them entirely.
  const facts = useMemo(() => {
    if (!data) return [];
    if (topP1) {
      return [topP1.location_name, topP1.fault_number, <DowntimeClock key="klok" since={topP1.created_at} />]
        .filter(Boolean);
    }
    const f = [];
    if (canView("storingen")) f.push(`${data.open_fault_count} open storingen`);
    if (canView("kalender") && data.today_task_count) f.push(`${data.today_task_count} taken vandaag`);
    if (canView("kalender") && data.overdue_task_count) f.push(`${data.overdue_task_count} achterstallig`);
    if (canView("punten") && (data.open_punt_count ?? 0)) f.push(`${data.open_punt_count} open punten`);
    return f.slice(0, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, topP1]);

  return (
    <Page console={{ variant: "view", title: "Dashboard", facts }}>
      <div className="space-y-4">
        {error ? (
          <ErrorState testid="dashboard-load-error" message={error} onRetry={load} />
        ) : loading || !data ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-4"><Skeleton className="h-16 w-full" /></Card>
            ))}
          </div>
        ) : (
          <>
            {topP1 && (
              <button
                type="button"
                data-testid="dashboard-p1-band"
                onClick={() => navigate(`/storingen/${topP1.id}`)}
                className="block w-full rounded bg-fout-tekst px-4 py-4 text-left text-white transition-colors hover:bg-balk-alarm sm:px-6"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-label text-mist">
                      <AlertTriangle size={14} weight="fill" /> Bedrijf staat stil — P1
                      {data.p1_count > 1 ? ` · ${data.p1_count} open` : ""}
                    </div>
                    <div className="mt-2 font-mono tabular-nums text-display leading-none">{topP1.fault_number}</div>
                    <div className="mt-2 truncate text-body text-mist">
                      {[topP1.location_name, topP1.description, ageText(topP1.created_at)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <span className="shrink-0 rounded bg-white px-3 py-2 text-body font-semibold text-fout-tekst">Openen</span>
                </div>
              </button>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {shown.map((t) => (
                <StatCard
                  key={t.testid} testid={t.testid} alarm={t.alarm} icon={t.icon}
                  label={t.label} value={t.value} tone={t.tone}
                  display={t.testid === displayTile}
                  onClick={t.onClick}
                />
              ))}
            </div>

            {/* One line — the dashboard is the morning read, not a report. */}
            {canView("werkbonnen") && (data.open_werkbon_count ?? 0) > 0 && (
              <button
                type="button"
                data-testid="dashboard-open-werkbonnen"
                onClick={() => navigate("/werkbonnen")}
                className="block text-left text-body text-primary hover:underline"
              >
                {data.open_werkbon_count === 1 ? "1 open werkbon" : `${data.open_werkbon_count} open werkbonnen`}
              </button>
            )}

            {emptyLabels.length > 0 && (
              <p className="text-body text-muted-foreground" data-testid="dashboard-nothing-open">
                Niets open bij {emptyLabels.length === 1
                  ? emptyLabels[0]
                  : `${emptyLabels.slice(0, -1).join(", ")} en ${emptyLabels[emptyLabels.length - 1]}`}.
              </p>
            )}

            {/* Scheduler state — a silent scheduler is worse than none. */}
            <Card className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid="scheduler-state">
              <div className="flex items-center gap-2 text-body text-foreground">
                <Bell size={18} className="text-muted-foreground" />
                <span>Planner: </span>
                {data.scheduler?.enabled ? (
                  <span className="font-medium text-goed-tekst">actief ({data.scheduler.timezone})</span>
                ) : (
                  <span className="font-medium text-muted-foreground">uit (preview) — geen automatische e-mail</span>
                )}
              </div>
              <span className="text-label text-muted-foreground" data-testid="scheduler-morning-ran">
                Ochtendoverzicht vandaag: {data.scheduler?.morning_ran_today ? "verstuurd" : "nog niet"}
              </span>
            </Card>

            {canView("kalender") && (data.today_task_count > 0 || data.overdue_task_count > 0) && (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card className="p-5" data-testid="dashboard-today-tasks">
                  <SectionHead icon={ListChecks} linkLabel="Kalender" onLink={() => navigate("/kalender")}>
                    Taken vandaag ({data.today_task_count})
                  </SectionHead>
                  {data.today_tasks.length === 0 ? (
                    <p className="text-body text-muted-foreground">Geen taken voor vandaag.</p>
                  ) : (
                    <ul className="space-y-2">
                      {data.today_tasks.map((t) => (
                        <li key={t.id} className="flex items-center gap-2 text-body">
                          <span className={`h-2 w-2 ${t.type === "vast_wekelijks" ? "bg-plaat" : t.type === "onderhoud" ? "border border-dotted border-plaat" : "border border-dashed border-plaat"}`} />
                          <span className={t.status === "gedaan" ? "text-muted-foreground line-through" : "text-foreground"}>{t.title}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>

                <Card className="p-5" data-testid="dashboard-overdue-tasks">
                  <SectionHead icon={Clock}>Achterstallig ({data.overdue_task_count})</SectionHead>
                  {data.overdue_tasks.length === 0 ? (
                    <p className="text-body text-muted-foreground">Niets achterstallig.</p>
                  ) : (
                    <ul className="space-y-2">
                      {data.overdue_tasks.map((t) => (
                        <li key={t.id} className="flex items-center justify-between gap-2 text-body">
                          <span className="text-foreground">{t.title}</span>
                          <Code size="small" className="text-destructive">{formatNlDate(t.due_date)}</Code>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            )}

            {canView("keuringen") && data.inspection_count > 0 && data.inspections.length > 0 && (
              <Card className="p-5" data-testid="dashboard-inspections">
                <SectionHead icon={ShieldCheck}>Keuringen verlopen of binnenkort ({data.inspection_count})</SectionHead>
                <ul className="space-y-2">
                  {data.inspections.slice(0, 10).map((i) => (
                    <li key={i.id} className="flex items-center justify-between gap-2 text-body">
                      <span className="text-foreground">{i.name}</span>
                      <div className="flex items-center gap-2">
                        {i.next_due && <Code size="small">{formatNlDate(i.next_due)}</Code>}
                        <InspectionStatusBadge status={i.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {canView("onderdelen") && (data.low_stock_count ?? 0) > 0 && data.low_stock_parts?.length > 0 && (
              <Card className="p-5" data-testid="dashboard-low-stock">
                <SectionHead icon={PackageMinus} linkLabel="Onderdelen" onLink={() => navigate("/onderdelen")}>
                  Onderdelen onder minimum ({data.low_stock_count ?? 0})
                </SectionHead>
                <ul className="space-y-2">
                  {data.low_stock_parts.slice(0, 10).map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 text-body" data-testid={`dashboard-low-stock-${p.id}`}>
                      <span className="text-foreground">{p.name}</span>
                      <Code size="small" className="text-destructive">{p.current_stock} / {p.min_stock} {p.unit}</Code>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {canView("punten") && (data.open_punt_count ?? 0) > 0 && data.open_punten?.length > 0 && (
              <Card className="p-5" data-testid="dashboard-punten">
                <SectionHead icon={ClipboardList} linkLabel="Alle punten" onLink={() => navigate("/mijn-werk")} testid="dashboard-punten-link">
                  Open punten ({data.open_punt_count ?? 0})
                </SectionHead>
                <ul className="space-y-2">
                  {data.open_punten.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 text-body" data-testid={`dashboard-punt-${p.id}`}>
                      <span className="text-foreground">{p.title}</span>
                      {/* A missing location renders nothing, never a dash. */}
                      {p.location_name && <span className="text-body text-muted-foreground">{p.location_name}</span>}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {canView("storingen") && data.open_faults.length > 0 && (
              <Card className="p-5">
                <SectionHead linkLabel="Alle storingen" onLink={() => navigate("/storingen")}>
                  Open storingen (P1 bovenaan)
                </SectionHead>
                {/* Phone: cards. Desktop: table. Same rule on every screen. */}
                <ul className="space-y-2 sm:hidden" data-testid="dashboard-faults-cards">
                  {data.open_faults.slice(0, 10).map((f) => (
                    <li key={f.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/storingen/${f.id}`)}
                        className="flex min-h-[72px] w-full flex-col justify-center gap-1 rounded border border-lijn bg-card p-3 text-left"
                      >
                        <div className="flex items-center gap-2">
                          <PriorityBadge priority={f.priority} />
                          <Code>{f.fault_number}</Code>
                        </div>
                        <div className="truncate text-body text-foreground">{f.description}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-body text-muted-foreground">{f.location_name}</span>
                          <FaultStatusBadge status={f.status} />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="hidden sm:block">
                  <Table data-testid="dashboard-faults-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Prioriteit</TableHead>
                        <TableHead>Nummer</TableHead>
                        <TableHead>Locatie</TableHead>
                        <TableHead>Omschrijving</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.open_faults.slice(0, 10).map((f) => (
                        <TableRow key={f.id} className="cursor-pointer" onClick={() => navigate(`/storingen/${f.id}`)}>
                          <TableCell><PriorityBadge priority={f.priority} /></TableCell>
                          <TableCell><Code>{f.fault_number}</Code></TableCell>
                          <TableCell>{f.location_name}</TableCell>
                          <TableCell>{f.description}</TableCell>
                          <TableCell><FaultStatusBadge status={f.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}

            {data.replacement.length > 0 && (
              <Card className="p-5">
                <SectionHead linkLabel="Alle assets" onLink={() => navigate("/assets")}>
                  Vervanging nodig / bijna
                </SectionHead>
                <ul className="space-y-2 sm:hidden" data-testid="dashboard-replacement-cards">
                  {data.replacement.slice(0, 10).map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/assets/${a.id}`)}
                        className="flex min-h-[72px] w-full flex-col justify-center gap-1 rounded border border-lijn bg-card p-3 text-left"
                      >
                        <Code>{a.asset_code}</Code>
                        <div className="truncate text-body text-foreground">{a.label}</div>
                        <div className="flex items-center gap-2">
                          {a.location_name && <span className="text-body text-muted-foreground">{a.location_name}</span>}
                          <ReplacementBadge status={a.replacement_status} date={a.replacement_date} />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="hidden sm:block">
                  <Table data-testid="dashboard-replacement-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Asset</TableHead>
                        <TableHead>Locatie</TableHead>
                        <TableHead>Vervangdatum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.replacement.slice(0, 10).map((a) => (
                        <TableRow key={a.id} className="cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)}>
                          <TableCell><Code>{a.asset_code}</Code></TableCell>
                          <TableCell>{a.label}</TableCell>
                          <TableCell>{a.location_name}</TableCell>
                          <TableCell><ReplacementBadge status={a.replacement_status} date={a.replacement_date} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
